import * as z from "zod";

import { sortTeam, TeamSchema } from "./team-model";

export const TeamInstanceSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  teamId: z.string(),
  ownerType: z.string(),
  ownerId: z.string(),
  team: TeamSchema.omit({ teamMembershipList: true }),
});

export type TeamInstance = z.infer<typeof TeamInstanceSchema>;

/**
 * Sorts two TeamInstance objects.
 * @param a First TeamInstance
 * @param b Second TeamInstance
 * @param desc Whether to sort in descending order
 * @returns -1, 0, or 1 based on the comparison
 */
export const sortTeamInstance = (
  a?: TeamInstance | null | undefined,
  b?: TeamInstance | null | undefined,
  desc: boolean = false,
): 0 | 1 | -1 => {
  if (!a && !b) {
    return 0;
  }

  if (!a) {
    return desc ? 1 : -1;
  }

  if (!b) {
    return desc ? -1 : 1;
  }

  return sortTeam(a.team, b.team, desc);
};

/**
 * Sorts two lists of TeamInstance objects.
 * The alphabetically first team in each list is used to determine the sorting order.
 * @param listA First list of TeamInstance objects
 * @param listB Second list of TeamInstance objects
 * @param desc Whether to sort in descending order
 * @returns -1, 0, or 1 based on the comparison
 */
export const sortTeamInstanceLists = (
  listA: TeamInstance[] = [],
  listB: TeamInstance[] = [],
  desc: boolean = false,
): 0 | 1 | -1 => {
  if (listA.length === 0 && listB.length === 0) {
    return 0;
  }

  if (listA.length === 0) {
    return desc ? 1 : -1;
  }

  if (listB.length === 0) {
    return desc ? -1 : 1;
  }

  // Sort both lists alphabetically by Team name
  const sortedA = [...listA].sort((a, b) => sortTeamInstance(a, b));
  const sortedB = [...listB].sort((a, b) => sortTeamInstance(a, b));

  // Compare the first elements of the sorted lists
  return sortTeamInstance(sortedA[0], sortedB[0], desc);
};
